/**
 * Break History Page (PR 1, Phase B1)
 *
 * Paginated history of all breaks across the team with filters:
 *   - User
 *   - Date range (from / to)
 *   - Break type
 *   - Flagged-only
 *
 * Route: /admin/hr/breaks/history
 * RBAC:  admin / team_leader / tax_team_leader / title_team_leader / manager
 *
 * CSV export is wired in PR 3; the button is intentionally absent here.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Loader2, ChevronRight, Coffee, RotateCcw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { BreakEmptyState, BreakPageHeader, BreakTableCard, BreakLoadingState, BreakErrorState, BreakPagination, BreakAvatar } from '../../components/hr/break/ui/BreakUi';
import {
  BreakStatusBadge,
  BreakDurationCell,
  PaidUnpaidPill,
  formatDateTimeShortET,
} from '../../components/hr/break/breakUiHelpers';
import BreakActivityTimeline from '../../components/hr/break/BreakActivityTimeline';

const API = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 25;

const defaultDateFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
};
const defaultDateTo = () => new Date().toISOString().split('T')[0];

const BreakHistoryPage = () => {
  // Phase 2 UX — accept ?date_from=, ?date_to=, ?status= so the KPI
  // strip's "Today's Breaks" / "Force-Ended Today" cards can deep-link
  // directly into a pre-filtered History view.
  const [searchParams] = useSearchParams();
  const initialDateFrom = searchParams.get('date_from') || defaultDateFrom();
  const initialDateTo = searchParams.get('date_to') || defaultDateTo();
  const initialStatus = searchParams.get('status') || '';

  const [breaks, setBreaks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Filters
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(initialDateFrom);
  const [dateTo, setDateTo] = useState(initialDateTo);
  const [breakTypeId, setBreakTypeId] = useState('all');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [breakTypes, setBreakTypes] = useState([]);
  const [users, setUsers] = useState([]);
  // PR 2 — Row-expand state. Only one break's timeline is expanded
  // at a time to keep the table scannable.
  const [expandedBreakId, setExpandedBreakId] = useState(null);
  // PR 3 — CSV export state. Single in-flight guard prevents double
  // clicks; we surface the row count returned by the server in the
  // success toast for confidence.
  const [exporting, setExporting] = useState(false);

  const fetchBreakTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API}/api/breaks/types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBreakTypes(resp.data.break_types || []);
    } catch (err) {
      console.error('Failed to load break types:', err);
    }
  }, []);

  const [scopeLabel, setScopeLabel] = useState('company');

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // BREAK-SCOPE-TEAM-001: scoped source — TLs get their Team Management
      // members only; Admin/HR get everyone. Same resolver as the data endpoints.
      const resp = await axios.get(`${API}/api/breaks/scope-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(resp.data.users || []);
      setScopeLabel(resp.data.scope || 'company');
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (userFilter) params.append('user_id', userFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (breakTypeId && breakTypeId !== 'all') params.append('break_type_id', breakTypeId);
      if (flaggedOnly) params.append('flagged_only', 'true');
      // Phase 2 UX — drill-through filter from KPI's Force-Ended card.
      if (statusFilter) params.append('status', statusFilter);

      const resp = await axios.get(`${API}/api/breaks/history?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBreaks(resp.data.breaks || []);
      setTotal(resp.data.total || 0);
      setPages(resp.data.pages || 1);
      setLoadError(null);
    } catch (err) {
      console.error('Failed to load break history:', err);
      setLoadError(err?.response?.data?.detail || err.message || 'Failed to load break history');
    } finally {
      setLoading(false);
    }
  }, [page, userFilter, dateFrom, dateTo, breakTypeId, flaggedOnly, statusFilter]);

  useEffect(() => { fetchBreakTypes(); fetchUsers(); }, [fetchBreakTypes, fetchUsers]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const resetFilters = () => {
    setUserFilter('');
    setDateFrom(defaultDateFrom());
    setDateTo(defaultDateTo());
    setBreakTypeId('all');
    setFlaggedOnly(false);
    setStatusFilter('');
    setPage(1);
  };

  // PR 3 — CSV export. Mirrors the on-screen filters exactly. Uses
  // axios responseType:'blob' so the auth header is sent (a plain
  // <a download> can't carry headers), then triggers a hidden-link
  // download. The filename comes from the backend's
  // Content-Disposition header.
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (userFilter) params.append('user_id', userFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (breakTypeId && breakTypeId !== 'all') params.append('break_type_id', breakTypeId);
      if (flaggedOnly) params.append('flagged_only', 'true');

      const resp = await axios.get(`${API}/api/breaks/history/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      // Parse Content-Disposition → filename (best-effort).
      const cd = resp.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(cd);
      const filename = match ? match[1] : 'break-history.csv';
      const rowCount = resp.headers['x-export-row-count'] || '?';

      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${rowCount} break${rowCount === '1' ? '' : 's'}`);
    } catch (err) {
      // For blob responses, the error body is a Blob — read it as text.
      let detail = 'Export failed';
      const blob = err.response?.data;
      if (blob && typeof blob.text === 'function') {
        try {
          const txt = await blob.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) detail = parsed.detail;
        } catch (_) { /* keep default */ }
      } else if (err.response?.data?.detail) {
        detail = err.response.data.detail;
      }
      toast.error(detail);
    } finally {
      setExporting(false);
    }
  }, [exporting, userFilter, dateFrom, dateTo, breakTypeId, flaggedOnly]);

  return (
    <>
      <BreakPageHeader title="Break History" subtitle="Filter by user, date range, type, or flagged-only to drill into specific scenarios." testId="history-header" />

      {/* Active status-filter chip (e.g. from KPI drill-through) */}
      {statusFilter && (
        <div className="mb-3 flex items-center gap-2 text-xs" data-testid="status-filter-chip">
          <span className="text-gray-500">Filtered to:</span>
          <span className="inline-flex items-center gap-1.5 font-medium bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2.5 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
            Status: {statusFilter.replace(/_/g, ' ')}
            <button
              type="button"
              onClick={() => { setStatusFilter(''); setPage(1); }}
              className="ml-1 text-rose-500 hover:text-rose-700"
              aria-label="Clear status filter"
              data-testid="clear-status-filter"
            >
              ×
            </button>
          </span>
        </div>
      )}

      {/* Filter bar */}
      <div className="bm-rise bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,.04)] px-4 py-3 mb-4" data-testid="history-filter-bar">
        {/* UI-BREAK-EXPORT-ALIGN-001 (Jul 2026): the actions column sizes to
            its content (auto) so Reset/Export never overflow the bar, and all
            controls share the h-9 baseline of the filter inputs. */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3">
          <div>
            <Label className="text-[10.5px] font-medium text-slate-500">User</Label>
            <Select value={userFilter || 'all'} onValueChange={(v) => { setUserFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="mt-1 h-9" data-testid="filter-user-select">
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-user-option-all">
                  {scopeLabel === 'company' ? 'All users' : `All users (my team · ${users.length})`}
                </SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`filter-user-option-${u.id}`}>
                    {u.full_name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10.5px] font-medium text-slate-500">From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="mt-1 h-9"
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <Label className="text-[10.5px] font-medium text-slate-500">To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="mt-1 h-9"
              data-testid="filter-date-to"
            />
          </div>
          <div>
            <Label className="text-[10.5px] font-medium text-slate-500">Break Type</Label>
            <Select value={breakTypeId} onValueChange={(v) => { setBreakTypeId(v); setPage(1); }}>
              <SelectTrigger className="mt-1 h-9" data-testid="filter-break-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {breakTypes.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-end justify-start xl:justify-end gap-2">
            <label className="flex h-9 items-center gap-2 cursor-pointer whitespace-nowrap">
              <Checkbox
                checked={flaggedOnly}
                onCheckedChange={(v) => { setFlaggedOnly(!!v); setPage(1); }}
                data-testid="filter-flagged-only"
              />
              <span className="text-sm">Flagged only</span>
            </label>
            <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="filter-reset" className="h-9">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting || loading || total === 0}
              data-testid="break-history-export-btn"
              className="h-9 whitespace-nowrap"
            >
              {exporting ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Exporting…</>
              ) : (
                <><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Results table */}
      {loading ? (
        <BreakTableCard><BreakLoadingState label="Loading history…" testId="history-loading" /></BreakTableCard>
      ) : loadError ? (
        <BreakErrorState title="Couldn't load break history." detail={loadError} onRetry={fetchHistory} testId="history-error" />
      ) : breaks.length === 0 ? (
        <BreakEmptyState tone="neutral" icon={<Coffee className="h-5 w-5" />} title="No breaks match these filters" subtitle="Adjust the user, date range or break type to see recorded breaks." testId="history-empty" />
      ) : (
        <>
          <BreakTableCard testId="history-table-wrapper">
            <Table className="tf-break-table">
              <TableHeader>
                <TableRow className="bm-thead">
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Date / Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Paid?</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {breaks.map((b) => {
                  const isOpen = expandedBreakId === b.id;
                  return (
                    <React.Fragment key={b.id}>
                      <TableRow data-testid={`history-row-${b.id}`} className={`bm-row ${isOpen ? 'is-open' : ''}`}>
                        <TableCell className="p-2">
                          <button
                            type="button"
                            onClick={() => setExpandedBreakId(isOpen ? null : b.id)}
                            className="text-gray-500 hover:text-gray-900 transition-colors"
                            data-testid={`history-row-toggle-${b.id}`}
                            aria-label={isOpen ? 'Collapse activity' : 'Expand activity'}
                          >
                            <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                          </button>
                        </TableCell>
                        <TableCell className="font-medium"><span className="flex items-center gap-2.5"><BreakAvatar name={b.user_name || b.user_id} tone="sky" />{b.user_name || b.user_id}</span></TableCell>
                        <TableCell className="text-xs text-gray-700">{formatDateTimeShortET(b.start_time)}</TableCell>
                        <TableCell className="text-xs text-gray-700">{b.end_time ? formatDateTimeShortET(b.end_time) : '—'}</TableCell>
                        <TableCell>{b.break_type_name}</TableCell>
                        <TableCell><PaidUnpaidPill isPaid={b.is_paid} /></TableCell>
                        <TableCell className="text-right">
                          <BreakDurationCell minutes={b.duration_minutes} maxMinutes={b.max_duration_minutes} />
                        </TableCell>
                        <TableCell><BreakStatusBadge status={b.status} flagged={b.flagged} waived={b.entitlement_waived} /></TableCell>
                        <TableCell className="text-xs text-gray-600 max-w-xs truncate" title={b.flagged_reason || b.force_end_reason || b.waived_reason || ''}>
                          {b.flagged_reason || b.force_end_reason || b.waived_reason || '—'}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="bg-slate-50/70 bm-expand" data-testid={`history-row-timeline-${b.id}`}>
                          <TableCell colSpan={9} className="py-3 px-6">
                            <div className="text-xs font-semibold text-gray-700 mb-2">Activity Timeline</div>
                            <BreakActivityTimeline breakId={b.id} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </BreakTableCard>

          <BreakPagination page={page} pages={pages} shown={breaks.length} total={total} noun={`break${total === 1 ? '' : 's'}`}
            onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(pages, p + 1))} testId="history-pagination" />
        </>
      )}
    </>
  );
};

export default BreakHistoryPage;
